import { Color, Spacing } from '@persona-id/design-tokens';
import { EyeIcon, EyeOffIcon } from '@persona-id/design-tokens/icons';
import { repeat } from 'lodash';
import { uniqueId } from 'lodash/fp';
import {
  ComponentPropsWithoutRef,
  ComponentType,
  createContext,
  FC,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useToggleButton } from 'react-aria';
import { useToggleState } from 'react-stately';
import styled, { css } from 'styled-components';

import IconButton from 'design-system/components/Button/IconButton';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { HelperTooltip } from 'design-system/components/Tooltip';
import { paragraphMixins } from 'design-system/mixins/text';

export type WordBreak = 'break-all' | 'break-word' | 'normal';
export type TextTransform = 'capitalize' | 'sentence';
export type HorizontalValue = boolean | 'full-width';

export const AttributeContext = createContext<{ horizontal?: HorizontalValue }>({});

// TODO: Create Toggle Button component
const IconButtonContainer = styled(IconButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    transition-duration: 150ms;
  }

  :hover {
    svg {
      color: ${Color.CorePurple800};
    }
  }
`;

interface AttributeValueProps {
  placeholder: ReactNode;
  show?: boolean;
  redacted?: boolean;
  value?: ReactNode;
  textTransform?: TextTransform;
  wordBreak?: WordBreak;
  'aria-labelledby': string;
  id: string;
}

interface ValueFormatterProps {
  isPlaceholder?: boolean;
  wordBreak?: WordBreak;
}

const Value = styled.dd<ValueFormatterProps>`
  ${paragraphMixins.md};
  color: ${({ isPlaceholder }) => (isPlaceholder ? Color.CoreSlate500 : Color.CoreSlate1000)};
  margin: 0;
  text-overflow: ellipsis;
  overflow-x: hidden;
  word-break: ${({ wordBreak }) => (wordBreak ? wordBreak : 'normal')};

  ul,
  ol {
    list-style-position: inside;
  }

  a {
    color: ${({ theme }) => theme.color.text.brand};
    text-decoration: none;

    &:visited {
      color: ${({ theme }) => theme.color.text.brand};
    }
  }
`;

export const AttributeValue = ({
  placeholder,
  show,
  redacted,
  value,
  wordBreak,
  id,
  'aria-labelledby': ariaLabelledBy,
}: AttributeValueProps) => {
  const isEmpty = value == null || value === '';

  const renderedVal = isEmpty ? (
    placeholder
  ) : (
    <>{!show ? repeat('•', typeof value === 'string' ? value.length : 8) : value}</>
  );

  return (
    <Value
      isPlaceholder={isEmpty}
      wordBreak={wordBreak}
      title={typeof value === 'string' ? value : undefined}
      aria-labelledby={ariaLabelledBy}
      id={id}
    >
      {redacted ? <Sticker id={id}>Redacted</Sticker> : renderedVal}
    </Value>
  );
};

export interface AttributeProps extends ComponentPropsWithoutRef<'div'> {
  /**
   * Title of the attribute. Rendered as a <dt>.
   */
  label: ReactNode;

  /**
   * Value to display. Rendered as a <dd>.
   */
  value?: ReactNode;

  /**
   * Will display a "Redacted" sticker if there is a value.
   */
  redacted?: boolean;

  /**
   * Renders toggleable eye button to the right of the label
   */
  toggleable?: boolean;

  /**
   * Text to display in presence of null value.
   */
  placeholder?: string;

  /**
   * Word break styling for value.
   */
  wordBreak?: WordBreak;

  /**
   * Render a custom label component
   */
  labelComponent?: FC<PropsWithChildren<{ id: string }>>;

  /**
   * Render a custom value component
   */
  valueComponent?: ComponentType<AttributeValueProps>;

  /**
   * Renders a HelperTooltip to display help text to the right of the label.
   */
  helpText?: ReactNode;

  /**
   * Custom adornments to display to the right of the label.
   */
  right?: ReactNode;

  /**
   * Renders attributes horizontally with label first.
   */
  horizontal?: HorizontalValue;
}

const AttributeContainer = styled('div')<{ horizontal?: HorizontalValue }>`
  ${({ horizontal }) =>
    horizontal &&
    css`
      display: table-row;

      > * {
        display: table-cell;
      }

      > ${AttributeLabel} {
        padding-right: ${Spacing[3]};
      }
    `}
`;

export const AttributeLabel = styled(Paragraph).attrs({
  size: 'md',
  color: 'tertiary',
  as: 'dt',
})`
  margin: 0;
  overflow-wrap: break-word;
`;

const RightContainer = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-left: ${Spacing[2]};
`;

const Attribute = ({
  label,
  value,
  redacted,
  placeholder = '-',
  wordBreak,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  labelComponent: LabelComponent = AttributeLabel,
  valueComponent: ValueComponent = AttributeValue,
  right,
  helpText,
  toggleable,
  horizontal,
  ...elemProps
}: AttributeProps) => {
  const attrCtx = useContext(AttributeContext);
  const toggleRef = useRef();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const valueAsArray = [].concat(value).filter(Boolean);
  const showToggleButton = toggleable && !redacted && valueAsArray.length > 0;
  const state = useToggleState({ defaultSelected: !toggleable });
  const { buttonProps } = useToggleButton(
    {
      'aria-label': state.isSelected ? `Show ${label} value` : `Hide ${label} value`,
      type: 'button',
      elementType: 'button',
    },
    state,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    toggleRef,
  );
  const labelId = useMemo(() => uniqueId('pds_attribute_label__'), []);
  const valueId = useMemo(() => uniqueId('pds_attribute_value__'), []);
  const show = state.isSelected;

  useEffect(() => {
    state.setSelected(!toggleable);
  }, [toggleable]);

  return (
    <AttributeContainer horizontal={horizontal || attrCtx?.horizontal} {...elemProps}>
      <LabelComponent id={labelId} aria-details={valueId}>
        {label}
        {helpText && (
          <RightContainer>
            <HelperTooltip size='sm'>{helpText}</HelperTooltip>
          </RightContainer>
        )}
        {showToggleButton && (
          <RightContainer>
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            <IconButtonContainer {...buttonProps} ref={toggleRef}>
              {show ? <EyeIcon /> : <EyeOffIcon />}
            </IconButtonContainer>
          </RightContainer>
        )}
        {right && <RightContainer>{right}</RightContainer>}
      </LabelComponent>
      <ValueComponent
        id={valueId}
        value={value}
        placeholder={placeholder}
        wordBreak={wordBreak}
        redacted={redacted}
        show={show}
        aria-labelledby={labelId}
      />
    </AttributeContainer>
  );
};

export default Attribute;
